import { userDataAtom } from '@/global-store';
import { useAuditEntriesWithFallback } from '@/hooks/useAuditEntriesWithFallback';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import { useRescanAuditEntries } from '@http/queries/useRescanAuditEntries';
import type { PolicyResponse } from '@http/queries/useFetchPolicies';
import { useNotification } from '@eye-able/ui';
import type { AuditData } from '@eye-able/types';
import { useAtomValue } from 'jotai';
import { useCallback } from 'react';

type RescannableAuditEntry = Pick<
  AuditData,
  'id' | 'domain' | 'maxRescans' | 'rescanCounter'
> & { scanImmediately?: AuditData['scanImmediately'] };

export function getRemainingRescans(entry: RescannableAuditEntry): number {
  return Math.max(0, (entry.maxRescans ?? 0) - (entry.rescanCounter ?? 0));
}

export function isAuditEntryRescanScheduled(
  entry: RescannableAuditEntry,
): boolean {
  return entry.scanImmediately === 1;
}

export function getPolicyApplicableAuditEntries(
  policy: Pick<PolicyResponse, 'domainScope' | 'auditEntryIds'>,
  auditEntries: RescannableAuditEntry[],
): RescannableAuditEntry[] {
  if (policy.domainScope === 'all-domains') {
    return auditEntries;
  }

  const applicableAuditEntryIds = new Set(policy.auditEntryIds);
  return auditEntries.filter((entry) => applicableAuditEntryIds.has(entry.id));
}

export function getPolicyRescannableAuditEntryIds(
  policy: Pick<PolicyResponse, 'domainScope' | 'auditEntryIds'>,
  auditEntries: RescannableAuditEntry[],
  isPrivilegedUser: boolean,
): number[] {
  return getPolicyApplicableAuditEntries(policy, auditEntries)
    .filter(
      (entry) =>
        !isAuditEntryRescanScheduled(entry) &&
        (isPrivilegedUser || getRemainingRescans(entry) > 0),
    )
    .map((entry) => entry.id);
}

export function isPolicyRescanDisabled(
  policy: Pick<PolicyResponse, 'domainScope' | 'status'>,
  auditEntryIds: number[],
  isPrivilegedUser: boolean,
): boolean {
  return (
    policy.status !== 'active' ||
    auditEntryIds.length === 0 ||
    (policy.domainScope === 'all-domains' && isPrivilegedUser)
  );
}

export function getPolicyRescanDisabledTooltipKey(
  policy: Pick<PolicyResponse, 'domainScope' | 'status'>,
  applicableAuditEntryIds: number[],
  rescannableAuditEntryIds: number[],
  isPrivilegedUser: boolean,
  areAllApplicableAuditEntriesScheduled = false,
): string | undefined {
  if (policy.status !== 'active') {
    return 'governanceInactivePolicyRescanTooltip';
  }
  if (policy.domainScope === 'all-domains' && isPrivilegedUser) {
    return 'governanceAllDomainsAdminRescanTooltip';
  }
  if (applicableAuditEntryIds.length === 0) {
    return 'governanceNoDomainsToRescanTooltip';
  }
  if (areAllApplicableAuditEntriesScheduled) {
    return 'alreadyScheduleCheckTooltip';
  }
  if (rescannableAuditEntryIds.length === 0) {
    return 'governanceRescanLimitReachedTooltip';
  }

  return undefined;
}

export function usePolicyRescan(referencedAuditEntryIds: number[] = []) {
  const { t } = useInlineTranslation();
  const notification = useNotification();
  const { auditEntries, isLoading: isLoadingReferencedAuditEntries } =
    useAuditEntriesWithFallback(referencedAuditEntryIds);
  const user = useAtomValue(userDataAtom);
  const rescanAuditEntries = useRescanAuditEntries();
  const isPrivilegedUser =
    user?.isAdmin === true || user?.isSalesAdmin === true;

  const getAuditEntryIds = useCallback(
    (policy: PolicyResponse) =>
      getPolicyRescannableAuditEntryIds(policy, auditEntries, isPrivilegedUser),
    [auditEntries, isPrivilegedUser],
  );
  const getApplicableAuditEntryIds = useCallback(
    (policy: PolicyResponse) =>
      getPolicyApplicableAuditEntries(policy, auditEntries).map(
        (entry) => entry.id,
      ),
    [auditEntries],
  );
  const areAllApplicableAuditEntriesScheduled = useCallback(
    (policy: PolicyResponse) => {
      const applicableAuditEntries = getPolicyApplicableAuditEntries(
        policy,
        auditEntries,
      );

      return (
        applicableAuditEntries.length > 0 &&
        applicableAuditEntries.every(isAuditEntryRescanScheduled)
      );
    },
    [auditEntries],
  );

  const rescanPolicy = useCallback(
    async (
      policy: PolicyResponse,
      {
        showSuccessNotification = true,
      }: { showSuccessNotification?: boolean } = {},
    ) => {
      const auditEntryIds = getAuditEntryIds(policy);

      if (isPolicyRescanDisabled(policy, auditEntryIds, isPrivilegedUser)) {
        return;
      }

      await rescanAuditEntries.mutateAsync({ auditEntryIds });
      if (showSuccessNotification) {
        notification.success(t('checkScheduleSuccess'));
      }
    },
    [getAuditEntryIds, isPrivilegedUser, notification, rescanAuditEntries, t],
  );

  return {
    getApplicableAuditEntryIds,
    getAuditEntryIds,
    areAllApplicableAuditEntriesScheduled,
    isPrivilegedUser,
    isRescanning:
      rescanAuditEntries.isPending || isLoadingReferencedAuditEntries,
    rescanPolicy,
  };
}
